import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { combineLatest, Observable, throwError, of, EMPTY } from 'rxjs';
import {
  catchError,
  concatMap,
  map,
  mergeMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import {
  ProjectSetup,
  SelectedSubproject,
  Subproject,
  UserSession,
} from '@shared/models';
import { AppFacade, UserDetailsFacade } from '@shared/state/facades';
import { OfflineFacade } from '@shared/state/facades/offline.facade';
import { isEmptyObject } from '@shared/utils';
import { CacheService, CryptoService, RoutingService } from '.';
import { PersistenceService } from './persistence';
import { DprDetails } from '@shared/models/dpr';
import { AuthService } from '@auth/services';

@UntilDestroy()
@Injectable()
export class CurrentUserDataService {
  private userSessionObservable: Observable<UserSession>;

  private accountId$ = this.userDetailsFacade.query.accountId$;
  private dprDetailsOfflineChanges$ =
    this.offlineFacade.query.dprDetailsOfflineChanges$;

  constructor(
    private userDetailsFacade: UserDetailsFacade,
    private appFacade: AppFacade,
    private offlineFacade: OfflineFacade,
    private persistenceService: PersistenceService,
    private router: Router,
    private cacheService: CacheService,
    private routingService: RoutingService,
    private cryptoService: CryptoService,
    private authService: AuthService
  ) {}

  getUserDetails(
    clientId: string,
    forceReload?: boolean
  ): Observable<UserSession> {
    const subprojectIdFromRoute = this.cryptoService.decryptId(
      this.routingService.subprojectIdFromCurrentRoute
    );

    return combineLatest([
      this.updateSelectedClient(clientId).pipe(take(1)),
      this.appFacade.query.isAppOnline$.pipe(take(1)),
      this.appFacade.query.loginPageDisplayed$.pipe(take(1)),
    ]).pipe(
      // load user details
      concatMap(([userDetails, isAppOnline, loginPageDisplayed]) => {
        const userSessionAndProjectSetupLoaded =
          userDetails && !isEmptyObject(userDetails.selectedSubproject?.data);

        const projectSetupReloadNeeded =
          userSessionAndProjectSetupLoaded &&
          !!subprojectIdFromRoute &&
          userDetails.selectedSubproject?.id !== subprojectIdFromRoute;

        if (
          userSessionAndProjectSetupLoaded &&
          !projectSetupReloadNeeded &&
          (!loginPageDisplayed || !isAppOnline) &&
          !forceReload
        ) {
          this.userSessionObservable = null;
          return combineLatest([of(null), of(isAppOnline)]);
        }

        if (projectSetupReloadNeeded && !isAppOnline) {
          this.goToChangeNotPossibleInOfflineErrorRoute();
          this.userSessionObservable = null;
          return combineLatest([of(null), of(isAppOnline)]);
        }

        if (this.userSessionObservable && !forceReload) {
          return combineLatest([this.userSessionObservable, of(isAppOnline)]);
        }

        if (loginPageDisplayed) {
          // we need to clear cache for the following reasons:
          // if there are no offline changes then dprs gets initialized and reads old data as getUsers session call
          // from main controller  is still pending or uncompleted (simultaneously calls)
          // this results in dpr getting initialized with outdated data
          this.cacheService.cleanCache();
        }

        this.appFacade.setLoginPageDisplayed(false);

        this.userSessionObservable =
          this.persistenceService.action.getUserSession();
        return combineLatest([this.userSessionObservable, of(isAppOnline)]);
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 400) {
          // Bad Request
          // the user is disabled, deleted or the email was not confirmed
          this.goToNoAccountOrNotVerifiedErrorRoute();
        }
        return EMPTY;
      }),
      tap(([data, isAppOnline]) => {
        if (!data || !isAppOnline) {
          // do not allow subproject change in offline mode
          return;
        }

        let subprojectId = data.selectedSubprojectId ?? data.subprojects[0]?.id;
        let subprojectToAddress: Subproject;

        if (
          subprojectIdFromRoute &&
          data.subprojects.some((s) => s.id === subprojectIdFromRoute)
        ) {
          subprojectId = subprojectIdFromRoute;
          if (subprojectIdFromRoute !== data.selectedSubprojectId) {
            data.selectedSubprojectId = subprojectIdFromRoute;
            subprojectToAddress = data.subprojects.find(
              (s) => s.id === subprojectId
            );
          }
        }

        // The session lands in the store before addressing the switch, so ClientId resolves to
        // this session's client rather than whatever was selected (or unset) before sign-in.
        this.userDetailsFacade.setUserSession(data);

        if (subprojectToAddress) {
          // no need to wait for this call to complete before proceeding
          this.persistenceService
            .updateSelectedSubproject(subprojectToAddress.id)
            .pipe(untilDestroyed(this))
            .subscribe();
        }

        if (subprojectId) {
          this.setSelectedSubproject(subprojectId, data);
        }
      }),

      // load project setup
      concatMap(([data, isAppOnline]) => {
        if (!data) {
          return combineLatest([of(null), of(null), of(null)]);
        }

        const subprojectId =
          data.selectedSubprojectId || data.subprojects[0]?.id;
        if (!subprojectId) {
          return combineLatest([of(null), of(null), of(null)]);
        }

        return combineLatest([
          of(data),
          this.persistenceService.action.getProjectSetup(),
          of(isAppOnline),
        ]);
      }),
      catchError((error: HttpErrorResponse) => {
        this.handleProjectSetupLoadError(error);
        return EMPTY;
      }),
      tap(([_userDetails, projectSetup]) => {
        if (projectSetup) {
          this.userDetailsFacade.setProjectSetup(projectSetup);
        }
      }),

      // load last three dprs
      concatMap(([, projectSetup, isAppOnline]) => {
        if (!projectSetup) {
          return combineLatest([of(null), of(null)]);
        }

        return combineLatest([this.setupLastThreeDprs(), of(isAppOnline)]);
      }),
      catchError((error: HttpErrorResponse) => {
        this.handleLastThreeDprsLoadError(error);
        return EMPTY;
      }),
      tap(([data, isAppOnline]) => {
        if (data && isAppOnline) {
          this.offlineFacade.setDprDetailsList(data);
          this.offlineFacade.setBackupLastDprs(null);
          this.offlineFacade.setDprDetails(null);
          this.offlineFacade.setBackupUserDetails(null);
        }
      }),
      mergeMap(() => {
        return this.userDetailsFacade.query.userDetails$;
      })
    ) as Observable<UserSession>;
  }

  updateSelectedClient(clientId: string): Observable<UserSession> {
    return this.userDetailsFacade.query.userDetails$.pipe(
      withLatestFrom(this.appFacade.query.isAppOnline$),
      take(1),
      mergeMap(([userDetails, isAppOnline]) => {
        if (!clientId || clientId === userDetails?.selectedClientId) {
          return of(userDetails);
        }

        if (!isAppOnline) {
          this.goToChangeNotPossibleInOfflineErrorRoute();
          return of(userDetails);
        }

        // This has to be updated first because it is used in the HttpInterceptor
        this.userDetailsFacade.setSelectedClientId(
          userDetails?.clientsList?.find((x) => x.id === clientId) ||
            !userDetails
            ? clientId
            : userDetails.selectedClientId
        );

        return this.persistenceService.updateSelectedClient(clientId);
      })
    );
  }

  setupSelectedSubproject(
    subprojectId: number,
    userDetails: UserSession
  ): Observable<UserSession> {
    this.setSelectedSubproject(subprojectId, userDetails);

    return this.persistenceService.action.getProjectSetup().pipe(
      catchError((error: HttpErrorResponse) => {
        this.handleProjectSetupLoadError(error);
        return throwError(() => new Error(error.error.message));
      }),
      tap((projectSetup: ProjectSetup) => {
        if (projectSetup) {
          this.userDetailsFacade.setProjectSetup(projectSetup);
        }
      }),

      // load last three dprs
      concatMap(() => {
        return this.setupLastThreeDprs();
      }),
      catchError((error: HttpErrorResponse) => {
        this.handleLastThreeDprsLoadError(error);
        return throwError(() => new Error(error.error.message));
      }),
      tap((data: DprDetails[]) => {
        this.offlineFacade.setDprDetailsList(data ?? []);
        this.offlineFacade.setBackupLastDprs(null);
        this.offlineFacade.setDprDetails(null);
        this.offlineFacade.setBackupUserDetails(null);
      }),
      mergeMap(() => {
        return this.userDetailsFacade.query.userDetails$;
      })
    );
  }

  /**
   * @description Checks if there are any offline changes made by the current accountId
   */
  detectIfSameUserChangedDataOffline(): Observable<boolean> {
    return this.dprDetailsOfflineChanges$.pipe(
      withLatestFrom(this.accountId$),
      take(1),
      map(([dprDetailsOfflineChanges, accountId]) => {
        return (
          (dprDetailsOfflineChanges?.length ?? false) &&
          dprDetailsOfflineChanges.some((x) => x.accountId === accountId)
        );
      })
    );
  }

  private goToNoAccountOrNotVerifiedErrorRoute(): void {
    this.router.navigate(['/error'], {
      queryParams: {
        errorType: 'no-account-or-not-verified',
      },
    });
  }

  private goToChangeNotPossibleInOfflineErrorRoute(): void {
    this.router.navigate(['/error'], {
      queryParams: {
        errorType: 'change-not-possible-in-offline',
      },
    });
  }

  private handleProjectSetupLoadError(error: HttpErrorResponse): void {
    combineLatest([
      this.offlineFacade.query.backupUserDetails$,
      this.userDetailsFacade.query.userDetails$,
    ]).pipe(
      tap(async ([backupUserDetails, userDetails]) => {
        if (
          backupUserDetails &&
          backupUserDetails.accountId === userDetails?.accountId
        ) {
          this.userDetailsFacade.setProjectSetup(
            backupUserDetails.selectedSubproject.data
          );
        } else if (error.status !== 403) {
          // Forbidden - handled by the http interceptor
          this.userDetailsFacade.setUserSession(null);
          await this.authService.signinRedirect();
        }
      })
    );
  }

  private handleLastThreeDprsLoadError(error: HttpErrorResponse): void {
    combineLatest([
      this.offlineFacade.query.backupLastDprs$,
      this.offlineFacade.query.backupUserDetails$,
      this.userDetailsFacade.query.userDetails$,
    ]).pipe(
      tap(async ([backupLastDprs, backupUserDetails, userSession]) => {
        if (
          backupLastDprs &&
          backupUserDetails &&
          backupUserDetails.accountId === userSession?.accountId
        ) {
          this.offlineFacade.setDprDetailsList(backupLastDprs);
        } else if (error.status !== 403) {
          // Forbidden - handled by the http interceptor
          this.userDetailsFacade.setUserSession(null);
          await this.authService.signinRedirect();
        }
      })
    );
  }

  private setSelectedSubproject(
    subprojectId: number,
    userDetails: UserSession
  ): void {
    const subproject = userDetails.subprojects.find(
      (s) => s.id === subprojectId
    );

    const selectedSubproject: SelectedSubproject = {
      id: subprojectId,
      name: subproject?.name ?? null,
      scopeName: subproject?.scopeName ?? null,
      vesselLocationName: subproject?.vesselLocationName ?? null,
      project: subproject?.project ?? null,
      dprScreen: subproject?.dprScreen,
      projectSetup: subproject?.projectSetup,
      isLocked: subproject?.isLocked ?? false,
      isReleased: subproject?.isReleased ?? false,
      // TODO(Improvement) remove canExportData from Storage and use userDetails.subprojects
      canExportData: subproject ? !!subproject.canExportData : false,
      canDownloadDprFiles: subproject
        ? !!subproject.canDownloadDprFiles
        : false,
      canEditExternalPartyComment: subproject
        ? !!subproject.canEditExternalPartyComment
        : false,
      canApprove: subproject ? !!subproject.canApprove : false,
      canAccessHiddenComments: subproject
        ? !!subproject.canAccessHiddenComments
        : false,
    };

    this.userDetailsFacade.setSelectedSubproject(selectedSubproject);
  }

  //#region Private methods

  private setupLastThreeDprs(): Observable<DprDetails[]> {
    return this.offlineFacade.query.dprDetailsList$.pipe(take(1));
  }

  //#endregion
}
